import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Ban, Lock } from 'lucide-react'
import type { ReactNode } from 'react'

import { loanAccountQueryOptions, loanCycleQueryOptions } from '@/api/loans-api'
import type { LoanAccountDetail, LoanCycleDetail, LoanCycleStatus } from '@/api/loans-types'
import { useAuth } from '@/auth/use-auth'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import type { PermissionCode } from '@/lib/permissions'

import { CycleBalances } from './cycle-panels'
import { cycleStatusLabel } from './constants'
import { LoanLink } from './loan-link'
import { loanPaths, useLoanParams } from './loan-paths'

/**
 * The shell every operation on a round is performed in.
 *
 * Each of these is its own screen rather than a dialog on the round, for the reason
 * docs/mobile-policy.md gives: they move money, forgive a debt, or reprice one, and every one of
 * them keys something a thumb should not be keying. A screen can refuse a narrow display; a
 * dialog inside a screen that adapts cannot.
 *
 * Before the form is reached, four questions are answered in order, because getting them in the
 * wrong order is how an operator fills in a form that could never have succeeded:
 *
 *   1. Is there room to do this safely? Otherwise it refuses, and says where to go instead.
 *   2. Does this user hold the permission? Otherwise a notice naming the code, not a blank form.
 *   3. Did the round load at all?
 *   4. Is the round in a state this applies to? Otherwise what state it is in, and what applies.
 *
 * The balances sit above the form on every one of them. Nobody should waive a penalty, settle a
 * round, or release money without the outstanding position in front of them.
 */
export interface CycleOperationContext {
  cycle: LoanCycleDetail
  account: LoanAccountDetail | undefined
  accountId: string
  cycleId: string
}

export interface CycleOperationScreenProps {
  /** Named the way the navigation names it, for the narrow-display panel. */
  screenName: string
  /** Why this one needs the room, in one line. */
  refusalReason: string
  title: string
  description: string
  permission: PermissionCode
  /** The states the domain accepts this operation in. The server refuses the rest anyway. */
  appliesTo: (status: LoanCycleStatus) => boolean
  /** What those states are, in words, for the panel that explains the refusal. */
  appliesToDescription: string
  children: (context: CycleOperationContext) => ReactNode
}

export function CycleOperationScreen({
  screenName,
  refusalReason,
  title,
  description,
  permission,
  appliesTo,
  appliesToDescription,
  children,
}: CycleOperationScreenProps) {
  const params = useLoanParams()
  const accountId = params.accountId ?? ''
  const cycleId = params.cycleId ?? ''

  const cycleQuery = useQuery(loanCycleQueryOptions(cycleId))
  const accountQuery = useQuery(loanAccountQueryOptions(accountId))

  const backTo = accountId && cycleId ? loanPaths.cycle(accountId, cycleId) : loanPaths.accounts

  return (
    <RequiresWiderScreen screen={screenName} reason={refusalReason}>
      <LoanLink
        to={backTo}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the round
      </LoanLink>

      {cycleQuery.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-2xl" />
        </div>
      ) : cycleQuery.isError ? (
        <ErrorSurface
          error={cycleQuery.error}
          onRetry={() => void cycleQuery.refetch()}
          title="This round could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={title}
            reference={cycleQuery.data.summary.displayReference}
            description={description}
            badges={<StatusBadge status={cycleQuery.data.summary.status} />}
          />

          <CycleBalances cycle={cycleQuery.data.summary} />

          <PermissionOrState
            permission={permission}
            status={cycleQuery.data.summary.status}
            appliesTo={appliesTo}
            appliesToDescription={appliesToDescription}
          >
            {children({
              cycle: cycleQuery.data,
              account: accountQuery.data,
              accountId,
              cycleId,
            })}
          </PermissionOrState>
        </>
      )}
    </RequiresWiderScreen>
  )
}

function PermissionOrState({
  permission,
  status,
  appliesTo,
  appliesToDescription,
  children,
}: {
  permission: PermissionCode
  status: LoanCycleStatus
  appliesTo: (status: LoanCycleStatus) => boolean
  appliesToDescription: string
  children: ReactNode
}) {
  const { hasPermission } = useAuth()
  const held = hasPermission(permission)

  if (!held) {
    return (
      <Alert tone="warning">
        <Lock aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Your role cannot do this</AlertTitle>
          <AlertDescription>
            This needs <span className="font-mono text-xs">{permission}</span>. The form is not
            shown rather than shown and refused — the server would decline it in any case, and a
            filled-in form that cannot be sent wastes the member&apos;s time as well as yours.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  if (!appliesTo(status)) {
    return (
      <Alert tone="info">
        <Ban aria-hidden />
        <div className="space-y-1">
          <AlertTitle>This does not apply to the round as it stands</AlertTitle>
          <AlertDescription>
            The round is <strong>{cycleStatusLabel(status)}</strong>. {appliesToDescription}
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return <>{children}</>
}
